import { useRef } from 'react';
import type { ListData } from '../../../../types';
import usePlayer from '../../contexts/usePlayer';

interface ExpandableBottomPanelProps {
  item: ListData;
  displayTitle?: string;
  isPlaylist?: boolean;
  onChaptersClick?: () => void;
}

// Star icon
export const StarIcon = ({
  size = 24,
  filled = false,
  color,
}: {
  size?: number;
  filled?: boolean;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={filled ? color || 'var(--teal)' : 'none'}
    stroke={color || (filled ? 'var(--teal)' : 'var(--primary-soft)')}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// Share icon
export const ShareIcon = ({
  size = 24,
  color,
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color || 'var(--primary-soft)'}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

// Horizontal scrubber for cover view
function CoverScrubber({
  currentTime,
  totalTime,
  isNowPlaying,
  seekTo,
  bufferPercent,
  isGenerating,
}: {
  currentTime: number;
  totalTime: number;
  isNowPlaying: boolean;
  seekTo: (time: number) => void;
  bufferPercent: number;
  isGenerating: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const progress = totalTime > 0 ? currentTime / totalTime : 0;
  const KNOB_SIZE = 12;

  const handleInteraction = (clientX: number) => {
    if (!trackRef.current || totalTime <= 0) return;
    const rect = trackRef.current.getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    );
    seekTo(percentage * totalTime);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isNowPlaying) return;
    isDragging.current = true;
    handleInteraction(e.clientX);
    const onMove = (ev: MouseEvent) => {
      if (isDragging.current) handleInteraction(ev.clientX);
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isNowPlaying) return;
    isDragging.current = true;
    handleInteraction(e.touches[0].clientX);
    const onMove = (ev: TouchEvent) => {
      if (isDragging.current) handleInteraction(ev.touches[0].clientX);
    };
    const onEnd = () => {
      isDragging.current = false;
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
  };

  return (
    <div style={{ padding: '8px 16px 0' }}>
      <div
        ref={trackRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          position: 'relative',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          cursor: isNowPlaying ? 'pointer' : 'default',
          touchAction: 'none',
        }}
      >
        {/* Track background */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: 'var(--border)',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          {/* Shimmer on unbuffered region when generating */}
          {isGenerating && bufferPercent < 100 && (
            <div
              style={{
                position: 'absolute',
                left: `${bufferPercent}%`,
                right: 0,
                height: '100%',
                backgroundImage:
                  'linear-gradient(90deg, rgba(59, 139, 128, 0.2) 0%, rgba(59, 139, 128, 0.5) 50%, rgba(59, 139, 128, 0.2) 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s infinite linear',
                borderRadius: 1.5,
              }}
            />
          )}
          {/* Buffered region (stays visible at 100% so track is teal when complete) */}
          {bufferPercent > 0 && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                width: `${bufferPercent}%`,
                height: '100%',
                backgroundColor: 'rgba(20, 184, 166, 0.35)',
                borderRadius: 1.5,
              }}
            />
          )}
          {/* Listened progress */}
          <div
            style={{
              position: 'relative',
              height: '100%',
              width: `${progress * 100}%`,
              backgroundColor: 'var(--teal)',
              borderRadius: 1.5,
            }}
          />
        </div>
        <div
          style={{
            position: 'absolute',
            left: `${progress * 100}%`,
            transform: 'translateX(-50%)',
            width: KNOB_SIZE,
            height: KNOB_SIZE,
            backgroundColor: 'var(--teal)',
            borderRadius: '50%',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}
        />
      </div>
    </div>
  );
}

// Format seconds as mm:ss or h:mm:ss
function formatTimeDisplay(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// List icon for chapters
const ListIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

export default function ExpandableBottomPanel({
  item,
  displayTitle,
  isPlaylist,
  onChaptersClick,
}: ExpandableBottomPanelProps) {
  const {
    nowPlayingId,
    currentTime: playerCurrentTime,
    seekTo,
    playbackRate,
  } = usePlayer();

  const isNowPlaying = nowPlayingId === item.id;

  const origin =
    item.type === 'content'
      ? item.author ||
        (item.sourceUrl
          ? (() => {
              try {
                const url = new URL(item.sourceUrl);
                return url.hostname.replace('www.', '');
              } catch {
                return '';
              }
            })()
          : '')
      : '';

  const totalTime = item.secondsTotal ?? 0;
  const displayTime = isNowPlaying
    ? playerCurrentTime
    : (item.secondsListened ?? 0);
  const titleToShow = displayTitle || item.title;
  const effectiveRate = playbackRate || 1;
  const remaining =
    totalTime > displayTime ? (totalTime - displayTime) / effectiveRate : 0;
  const rHrs = Math.floor(remaining / 3600);
  const rMins = Math.floor((remaining % 3600) / 60);
  const timeLeftStr =
    rHrs > 0
      ? `${rHrs}h ${rMins}m left (${effectiveRate.toFixed(1)}x)`
      : `${rMins}m left (${effectiveRate.toFixed(1)}x)`;

  return (
    <div className="expandable-panel-container">
      {/* Title */}
      <div style={{ textAlign: 'center', padding: '8px 16px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {isPlaylist && onChaptersClick && (
            <button
              onClick={onChaptersClick}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary)',
                flexShrink: 0,
              }}
              aria-label="Open chapters"
            >
              <ListIcon size={18} />
            </button>
          )}
          <h3
            style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}
          >
            {titleToShow}
          </h3>
        </div>
        {origin && (
          <div
            style={{
              marginTop: '4px',
              fontSize: '0.9rem',
              color: 'var(--primary-soft)',
            }}
          >
            {item.sourceUrl ? (
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--teal)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                }}
              >
                {origin}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--teal)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            ) : (
              <span>{origin}</span>
            )}
          </div>
        )}
      </div>

      {/* Scrubber */}
      <CoverScrubber
        currentTime={displayTime}
        totalTime={totalTime}
        isNowPlaying={isNowPlaying}
        seekTo={seekTo}
        bufferPercent={
          item.status === 'pending'
            ? 0
            : totalTime > 0 && item.secondsGenerated != null
              ? Math.min((item.secondsGenerated / totalTime) * 100, 100)
              : item.status === 'completed'
                ? 100
                : 0
        }
        isGenerating={item.status === 'processing' || item.status === 'pending'}
      />

      {/* Time display */}
      {totalTime > 0 ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '2px 16px 6px',
            fontSize: '0.75rem',
            color: 'var(--primary-soft)',
          }}
        >
          <span>{formatTimeDisplay(displayTime)}</span>
          <span>{timeLeftStr}</span>
          <span>{formatTimeDisplay(totalTime)}</span>
        </div>
      ) : item.status === 'pending' ? (
        <div
          style={{
            padding: '4px 16px 6px',
            fontSize: '0.75rem',
            color: 'var(--primary-soft)',
            textAlign: 'center',
          }}
        >
          Loading article...
        </div>
      ) : null}
    </div>
  );
}
